import { onKeyDownSubmit } from 'accessibility/eventHandlers'
import PresenceAvatar from 'components/PresenceAvatar'
import RecipientAvatar from 'components/RecipientAvatar'
import useConversationHistoryToggle from 'hooks/useConversationHistoryToggle'
import useLastMessage from 'hooks/useLastMessage'
import { useRecipientResolution } from 'hooks/useRecipientResolution'
import useUserResolution from 'hooks/useUserResolution'
import { triggerConversationSelectedAPIEvent } from 'modules/api/events'
import { clearActiveCampaign } from 'modules/campaigns/actions'
import { setActiveConversation } from 'modules/conversations/actions'
import DateTimeManager from 'modules/dateTime'
import { isEndUserMessage } from 'modules/message/utils'
import { isEmpty, pathOr } from 'ramda'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Message } from 'types/message'

import './style.css'

const LINE_BREAKS = /\r?\n|\r/g

const ConversationHistoryListItem = ({ conversation = {} }: { conversation?: Record<string, any> }) => {
  const {
    onToggleConversationHistory
  } = useConversationHistoryToggle()

  const { t } = useTranslation()

  const dispatch = useDispatch()
  const endUser = useUserResolution()
  const conversationId = useMemo(() => pathOr(null, ['conversation', 'id'], conversation), [conversation])

  const lastMessage: Partial<Message> = useLastMessage(conversationId)
  const lastMessageAuthorId = useMemo(() => pathOr(null, ['authorId'], lastMessage), [lastMessage])

  const { recipient: agent } = useRecipientResolution(lastMessageAuthorId)
  const isEndUser = isEndUserMessage(lastMessage)

  const toggleConversation = (id: number) => () => {
    if (!id) {
      return
    }

    onToggleConversationHistory(false)

    dispatch(clearActiveCampaign())
    dispatch(setActiveConversation({
      conversationId: id
    }))
    triggerConversationSelectedAPIEvent(id)
  }

  if (isEmpty(lastMessage)) {
    return null
  }

  const { createdAt, attributes = {} } = lastMessage
  const isToday = DateTimeManager.isTimeToday(createdAt)
  const timestamp = isToday
    ? DateTimeManager.unixFormatDateTime(createdAt, t('dt.format.time.short'))
    : DateTimeManager.unixFormatDateTime(createdAt, t('dt.format.date.short'))

  const name = isEndUser ? endUser.name : agent.name
  const hasPreview = !!attributes?.preview?.replace(LINE_BREAKS, '')?.trim()
  const timestampLabel = `${isToday ? `today at ${timestamp}` : `on ${timestamp}`}`
  const convoPreviewLabel = `${hasPreview ? `Conversation preview: ${attributes.preview}.` : ''}`
  const ariaLabel = `Click to open conversation with ${name} ${timestampLabel}. ${convoPreviewLabel}`

  return (
    <li className="drift-widget-conversation-list-item">
      <div
        aria-label={ariaLabel}
        className="drift-widget-conversation-list-item-body"
        onClick={toggleConversation(conversation.conversation.id)}
        onKeyDown={onKeyDownSubmit(toggleConversation(conversation.conversation.id))}
        role="button"
        tabIndex={0}
      >
        {!isEndUser
          ? <PresenceAvatar size="SMALL" className="drift-widget-conversation-list-item-avatar" image={agent.avatarUrl} id={lastMessageAuthorId}/>
          : <RecipientAvatar size="SMALL" className="drift-widget-conversation-list-item-avatar" image={endUser.avatar}/>
        }
        <div className="drift-widget-conversation-list-item-content">
          <h2>{name}</h2>
          <span className="drift-widget-conversation-list-item-time">{timestamp}</span>
          <p className="drift-widget-conversation-list-item-last-message">
            {attributes.preview}
          </p>
        </div>
      </div>
    </li>
  )

}

export default ConversationHistoryListItem
